// ClickUp-style inline-edit primitive — FE-043b.
//
// Wraps a label + value display that, on click, swaps the value
// for an input/textarea/select. Save fires onSave with the parsed
// new value; Cancel restores the previous render. Save errors flow
// back through ``error`` so the caller can surface them next to
// the field without dictating layout.

import { Check, Pencil, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";


export type InlineEditMode = "text" | "textarea" | "select" | "datetime";


export interface InlineSelectOption {
  value: string;
  label: string;
}


export interface InlineEditableFieldProps {
  label: string;
  /** Stable id for testid namespacing. */
  fieldId: string;
  /** Current persisted value as a string (empty string ≈ unset). */
  value: string;
  /** Pretty rendering of the current value when not editing. */
  display: ReactNode;
  mode: InlineEditMode;
  /** Required for ``mode="select"``. */
  options?: InlineSelectOption[];
  placeholder?: string;
  /** Called on submit with the trimmed new value. Resolve to
   * close the editor; reject (or set ``error``) to keep it open. */
  onSave: (next: string) => void | Promise<void>;
  isPending?: boolean;
  error?: string | null;
  /** Disables the click affordance entirely (e.g. terminal status
   * fields that need a different control). */
  readOnly?: boolean;
  /** Optional override for the input/textarea/select element so
   * callers can lift the editor to the typography of the value
   * being edited (e.g. the page title needs heading-scale text). */
  inputClassName?: string;
}


export function InlineEditableField({
  label, fieldId, value, display, mode, options, placeholder,
  onSave, isPending, error, readOnly, inputClassName,
}: InlineEditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >(null);

  useEffect(() => {
    if (editing) setDraft(value);
  }, [editing, value]);

  useEffect(() => {
    if (editing) requestAnimationFrame(() => inputRef.current?.focus());
  }, [editing]);

  function commit(): void {
    const trimmed = mode === "textarea" ? draft : draft.trim();
    if (trimmed === value.trim()) {
      setEditing(false);
      return;
    }
    Promise.resolve(onSave(trimmed)).then(
      () => setEditing(false),
      () => {/* keep open; caller surfaces ``error`` */},
    );
  }

  function cancel(): void {
    setDraft(value);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div
        data-testid={`inline-field-${fieldId}`}
        className="group flex flex-col gap-1"
      >
        {label && (
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
            {label}
          </span>
        )}
        <button
          type="button"
          data-testid={`inline-field-${fieldId}-trigger`}
          disabled={readOnly}
          onClick={() => setEditing(true)}
          className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-left text-sm text-text-primary hover:bg-recessed/60 disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span className="min-w-0 flex-1 truncate">{display}</span>
          {!readOnly && (
            <Pencil
              size={12}
              aria-hidden="true"
              className="flex-shrink-0 text-text-tertiary opacity-0 transition-fast ease-out group-hover:opacity-100"
            />
          )}
        </button>
        {error && (
          <span
            role="alert"
            data-testid={`inline-field-${fieldId}-error`}
            className="text-[11px] font-medium text-ember-ink"
          >
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`inline-field-${fieldId}-editor`}
      className="flex flex-col gap-1"
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <div className="flex items-start gap-1">
        {mode === "textarea" ? (
          <textarea
            ref={(el) => { inputRef.current = el; }}
            data-testid={`inline-field-${fieldId}-input`}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
            }}
            rows={4}
            className={`flex-1 rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary${inputClassName ? ` ${inputClassName}` : ""}`}
          />
        ) : mode === "select" ? (
          <select
            ref={(el) => { inputRef.current = el; }}
            data-testid={`inline-field-${fieldId}-input`}
            value={draft}
            onChange={(e) => {
              // ClickUp-style 1-click save: a select picks
              // immediately. Skip the explicit save button.
              const next = e.target.value;
              setDraft(next);
              if (next === value.trim()) {
                setEditing(false);
                return;
              }
              Promise.resolve(onSave(next)).then(
                () => setEditing(false),
                () => {/* keep open */},
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
            }}
            className={`flex-1 rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary${inputClassName ? ` ${inputClassName}` : ""}`}
          >
            {(options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <input
            ref={(el) => { inputRef.current = el; }}
            data-testid={`inline-field-${fieldId}-input`}
            type={mode === "datetime" ? "datetime-local" : "text"}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter") commit();
            }}
            onBlur={() => {
              // 1-click save: blur commits the value (Enter still
              // works). Esc cancels via keydown above.
              commit();
            }}
            className={`flex-1 rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary${inputClassName ? ` ${inputClassName}` : ""}`}
          />
        )}
        <button
          type="button"
          data-testid={`inline-field-${fieldId}-save`}
          disabled={isPending}
          onClick={commit}
          className="rounded-md bg-teal p-1 text-text-inverse hover:opacity-90 disabled:opacity-60"
          aria-label="Save"
        >
          <Check size={12} aria-hidden="true" />
        </button>
        <button
          type="button"
          data-testid={`inline-field-${fieldId}-cancel`}
          onClick={cancel}
          className="rounded-md border border-border-subtle p-1 text-text-secondary hover:text-text-primary"
          aria-label="Cancel"
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>
      {error && (
        <span
          role="alert"
          data-testid={`inline-field-${fieldId}-error`}
          className="text-[11px] font-medium text-ember-ink"
        >
          {error}
        </span>
      )}
    </div>
  );
}
